import { MIN_IMAGES_PER_LISTING, formatMYR, type IntakeDraft } from './aiIntake'
import { areaReading, type AreaKey } from './area'

/**
 * The review's layout of the form: which fields are shown under which
 * heading, which the form insists on, and how each is filled in.
 *
 * Every row here is a field the listing form actually sends. What the AI
 * reads off a title but the form has no home for (express conditions,
 * restrictions in interest, land use, the lease expiry) stays in the
 * conversation and the summary rather than being shown as a field that goes
 * nowhere.
 */
/** An 'area' is a number with its unit picked beside it (see ./area). */
export type RowKind = 'select' | 'input' | 'number' | 'area' | 'bool' | 'file'

export interface Row {
    key: string
    label: string
    kind: RowKind
    /** The form will not submit without it, so an empty value is always shown. */
    required?: boolean
    format?: (value: string | number) => string
    /** For a 'select': the form's list to pick from, and the field its ID goes in. */
    choices?: ChoiceList
    idKey?: string
    /** Shown, and required, only when this says so. */
    when?: (draft: IntakeDraft, agent: AgentContext) => boolean
    /** Units the form keeps beside the number, e.g. sqft. */
    suffix?: string
    /** Why this value is not usable, e.g. a postcode that is not five digits. */
    validate?: (value: unknown) => string | null
}

/** What the form shows depends a little on who the agent is. */
export interface AgentContext {
    isInternal: boolean
}

/** A list in `/listings/criterias`, or the sub-types of the chosen type. */
export type ChoiceList =
    | 'listingTypes'
    | 'projectTypes'
    | 'forSaleOrRentOptions'
    | 'rentalTypes'
    | 'propertyTypes'
    | 'propertySubTypes'
    | 'propertyUnitTypes'
    | 'tenures'
    | 'titleTypes'
    | 'furnishingConditions'
    | 'zoning'

/**
 * What the form shows depends on what has been chosen, and the review
 * follows the same rules, so a field only appears where the form has one.
 * These are the sub-type lists the form itself tests against
 * (src/pages/listings/application/UnitDetails.tsx and PropertyDetails.tsx).
 */
/** Land with no building: no bathrooms, no built-up. */
const NO_BUILDING = [4, 18, 30, 32, 38, 39]
/** Land or land-like: no parking either. */
const NO_PARKING = [4, 18, 32, 38, 39, 40]
/** Bare land: nothing to furnish. */
const NO_FURNISHING = [4, 18]
/** A unit inside a building: no land area of its own. */
const NO_LAND_AREA = [19, 20, 21, 22, 23, 24, 25, 26]
/** No unit type to pick for these. */
const NO_UNIT_TYPE = [4, 18, 29, 30, 32, 38, 39]

const subTypeId = (d: IntakeDraft) => {
    const own = d.fields.propertySubTypeId
    if (typeof own === 'number' && own > 0) return own
    const fromAI = d.category.ids.propertySubTypeId
    return typeof fromAI === 'number' && fromAI > 0 ? fromAI : null
}
const listingTypeId = (d: IntakeDraft) => Number(d.fields.listingTypeId) || Number(d.category.ids.listingTypeId) || null
/** Shown until a sub-type says otherwise, then only where the form shows it. */
const unless = (hidden: number[]) => (d: IntakeDraft) => {
    const id = subTypeId(d)
    return id === null || !hidden.includes(id)
}
/** The form asks for bedrooms and maid rooms on residential listings only. */
const isResidential = (d: IntakeDraft) => listingTypeId(d) === 1
const isSubsale = (d: IntakeDraft) => Number(d.fields.projectTypeId) === 1 || Number(d.category.ids.projectTypeId) === 1

/** Residential lettings are the only ones the form asks a rental type for. */
const isResidentialRent = (d: IntakeDraft) => listingTypeId(d) === 1 && Number(d.fields.forSaleOrRentId) === 2

export const SECTIONS: { title: string; rows: Row[] }[] = [
    {
        title: 'Listing',
        rows: [
            { key: 'listingType', label: 'Listing category', kind: 'select', required: true, choices: 'listingTypes', idKey: 'listingTypeId' },
            { key: 'purpose', label: 'For sale or rent', kind: 'select', required: true, choices: 'forSaleOrRentOptions', idKey: 'forSaleOrRentId' },
            { key: 'rentalType', label: 'Rental type', kind: 'select', required: true, choices: 'rentalTypes', idKey: 'rentalTypeId', when: isResidentialRent },
            { key: 'projectType', label: 'Project type', kind: 'select', required: true, choices: 'projectTypes', idKey: 'projectTypeId' },
            { key: 'forAuction', label: 'Going to auction', kind: 'bool' },
            { key: 'forTender', label: 'Going to tender', kind: 'bool' },
            { key: 'coAgencyListing', label: 'Co-agency listing', kind: 'bool' },
            { key: 'coShareListing', label: 'Co-share internally', kind: 'bool', when: (_d, agent) => agent.isInternal },
        ],
    },
    {
        title: 'Property',
        rows: [
            { key: 'propertyType', label: 'Property type', kind: 'select', required: true, choices: 'propertyTypes', idKey: 'propertyTypeId' },
            { key: 'propertySubType', label: 'Property sub-type', kind: 'select', required: true, choices: 'propertySubTypes', idKey: 'propertySubTypeId' },
            { key: 'unitType', label: 'Unit type', kind: 'select', choices: 'propertyUnitTypes', idKey: 'propertyUnitTypeId', when: unless(NO_UNIT_TYPE) },
            { key: 'tenure', label: 'Tenure', kind: 'select', required: true, choices: 'tenures', idKey: 'tenureId' },
            { key: 'titleType', label: 'Title type', kind: 'select', required: true, choices: 'titleTypes', idKey: 'titleTypeId' },
            { key: 'zoning', label: 'Zoning', kind: 'select', choices: 'zoning', idKey: 'zoningId' },
            { key: 'yearOfCompletion', label: 'Year completed', kind: 'number', required: true, when: isSubsale },
            { key: 'bumiLot', label: 'Bumi lot', kind: 'bool' },
        ],
    },
    {
        title: 'Location',
        rows: [
            { key: 'rawAddress', label: 'Address', kind: 'input', required: true },
            { key: 'state', label: 'State', kind: 'select', required: true },
            { key: 'city', label: 'City', kind: 'select', required: true },
            { key: 'township', label: 'Township', kind: 'input' },
            { key: 'street', label: 'Street', kind: 'input' },
            { key: 'unitNumber', label: 'Unit number', kind: 'input' },
            {
                key: 'postcode',
                label: 'Postcode',
                kind: 'input',
                required: true,
                // Malaysian postcodes are five digits; the form and the API take nothing else.
                validate: (v) => (/^\d{5}$/.test(String(v ?? '').trim()) ? null : 'must be 5 digits'),
            },
            {
                key: 'radius',
                label: 'Area circle',
                kind: 'number',
                required: true,
                suffix: 'm',
                // The same limits the circle on the map is drawn within.
                validate: (v) => (Number(v) >= 50 && Number(v) <= 5000 ? null : 'must be between 50 and 5,000 m'),
            },
        ],
    },
    {
        title: 'Size and rooms',
        rows: [
            { key: 'landArea', label: 'Land area', kind: 'area', when: unless(NO_LAND_AREA) },
            { key: 'builtUp', label: 'Built-up', kind: 'area', required: true, when: unless(NO_BUILDING) },
            { key: 'noOfBedrooms', label: 'Bedrooms', kind: 'number', required: true, when: (d) => isResidential(d) && unless(NO_BUILDING)(d) },
            { key: 'noOfBathrooms', label: 'Bathrooms', kind: 'number', required: true, when: unless(NO_BUILDING) },
            { key: 'noOfParkingSpots', label: 'Parking', kind: 'number', when: unless(NO_PARKING) },
            { key: 'noOfMaidrooms', label: 'Maid rooms', kind: 'number', when: (d) => isResidential(d) && unless(NO_BUILDING)(d) },
            { key: 'furnishing', label: 'Furnishing', kind: 'select', choices: 'furnishingConditions', idKey: 'furnishingConditionId', when: unless(NO_FURNISHING) },
            { key: 'maintenanceFee', label: 'Maintenance fee', kind: 'number', when: unless(NO_BUILDING), format: (v) => (typeof v === 'number' ? `${formatMYR(v)} / month` : String(v)) },
        ],
    },
    {
        // What the form calls Land Details, and insists on before it submits.
        // A land title carries all of it, so the AI usually fills it in.
        title: 'Land details',
        rows: [
            { key: 'lotNo', label: 'Lot No.', kind: 'input', required: true },
            { key: 'district', label: 'Daerah', kind: 'select', required: true },
            { key: 'subdistrict', label: 'Bandar/Pekan/Mukim', kind: 'select', required: true },
            { key: 'sizeOfLand', label: 'Size of land', kind: 'area', required: true },
            { key: 'titleTypeCode', label: 'Jenis Hakmilik', kind: 'select', required: true, idKey: 'landTitleTypeId' },
            { key: 'ownershipNumber', label: 'No. Hakmilik', kind: 'input', required: true },
            { key: 'jupemNo', label: 'JUPEM / MyLot No.', kind: 'input' },
            { key: 'proof', label: 'Proof of ownership', kind: 'file', required: true },
        ],
    },
]

const NEEDS_ID = ['state', 'city']

export const isEmpty = (v: unknown) => v === null || v === undefined || v === ''

/** The rows shown for this draft: a conditional one only when it applies. */
export const rowsFor = (rows: Row[], draft: IntakeDraft, agent: AgentContext) => rows.filter((r) => !r.when || r.when(draft, agent))

/** A picked value counts when either its name or the form's ID for it is set. */
const answered = (draft: IntakeDraft, row: Row) => {
    // The form is sent the state's and city's ID, not their name, so a name
    // that matched nothing in the form's list has not answered the question.
    if (NEEDS_ID.includes(row.key)) return typeof draft.fields[`${row.key}Id`] === 'number'
    // An area is a number: text that is not one has not answered it.
    if (row.kind === 'area') return areaReading(draft.fields, row.key as AreaKey).sqft !== null
    return !isEmpty(draft.fields[row.key]) || (row.idKey ? !isEmpty(draft.fields[row.idKey]) : false)
}

/** The complaint about a filled-in value, if it has one. */
export const problemWith = (draft: IntakeDraft, row: Row) => (row.validate && answered(draft, row) ? row.validate(draft.fields[row.key]) : null)

/** Google gives a point; the form needs one before it will submit. */
const hasPin = (d: IntakeDraft) => typeof d.fields.lat === 'number' && typeof d.fields.lng === 'number'

/**
 * What the form will still insist on for this draft, by name. The photos
 * count is passed in because the gallery lives on the page, not the draft.
 */
export const requiredLeft = (draft: IntakeDraft, images = MIN_IMAGES_PER_LISTING, agent: AgentContext = { isInternal: false }, hasProof = false): string[] => [
    ...(draft.fields.headline ? [] : ['Headline']),
    ...(draft.pricing.status === 'stated' ? [] : ['Asking price']),
    ...SECTIONS.flatMap((s) => rowsFor(s.rows, draft, agent))
        .filter((r) => r.kind !== 'file' && ((r.required && !answered(draft, r)) || problemWith(draft, r)))
        .map((r) => (problemWith(draft, r) ? `${r.label} (${problemWith(draft, r)})` : r.label)),
    ...(hasPin(draft) ? [] : ['Map location']),
    ...(draft.fields.description ? [] : ['Description']),
    ...(images >= MIN_IMAGES_PER_LISTING ? [] : [`Photos (${images} of ${MIN_IMAGES_PER_LISTING})`]),
    ...(hasProof ? [] : ['Proof of ownership']),
]

/** How many things the form will still want for this draft. */
export const toFillFor = (draft: IntakeDraft, images?: number, agent?: AgentContext, hasProof?: boolean) => requiredLeft(draft, images, agent, hasProof).length
